import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Mail, Phone, Instagram, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Section } from '../components/shared/Section';
import { GlassCard } from '../components/shared/GlassCard';
import { Particles } from '../components/shared/Particles';
import { SEO } from '../components/shared/SEO';
import { CONTACT_INFO, SERVICE_TYPES } from '../lib/constants';
import { useEffect } from 'react';
import ReactGA from "react-ga4";

// Direct CRM API endpoint - no backend needed
const CRM_API_URL = 'https://studio-crm.hydroxmusic.com/api/leads';


const BUDGET_RANGES = [
  'Under ₹1L',
  '₹1L - ₹5L',
  '₹5L - ₹10L',
  '₹10L - ₹25L',
  'Above ₹25L',
];

const TIMELINE_OPTIONS = [
  '1-2 weeks',
  '2-4 weeks',
  '1-2 months',
  '2-3 months',
  'Flexible',
];

const PageHero = () => {
  return (
    <section className="relative min-h-[40vh] flex items-center justify-center overflow-hidden pt-20">
      <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent" />
      <Particles count={15} />

      <div className="relative z-10 text-center px-6 max-w-4xl">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-sm uppercase tracking-[0.3em] text-primary font-manrope mb-6"
        >
          Contact
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="font-syne text-4xl sm:text-5xl md:text-6xl font-bold text-white tracking-tight mb-6"
        >
          Get a Quote
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="font-manrope text-base md:text-lg text-muted-foreground max-w-2xl mx-auto"
        >
          Tell us about your project and we'll get back to you with a detailed proposal.
        </motion.p>
      </div>
    </section>
  );
};

const ContactForm = () => {

  const [serviceTypes, setServiceTypes] = useState([]);

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    service_type_id: '',
    budget_range: '',
    timeline_id: '',
    project_description: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await fetch('https://studio-crm.hydroxmusic.com/api/master/service-types');
        const data = await res.json();
        setServiceTypes(data);
      } catch (err) {
        console.error('Failed to load services');
      }
    };

    fetchServices();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const payload = {
        full_name: formData.full_name,
        email: formData.email,
        service_type_id: formData.service_type_id,
        project_description: formData.project_description,
        source: 'Website',
      };

      if (formData.phone) payload.phone = formData.phone;
      if (formData.budget_range) payload.budget_range = formData.budget_range;
      if (formData.timeline_id) payload.timeline_id = formData.timeline_id;

      const response = await fetch('https://studio-crm.hydroxmusic.com/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
  const errData = await response.json();
  throw new Error(errData.detail || 'Failed to submit form');
}

// SUCCESS SECTION
setIsSuccess(true);

if (!response.ok) {
  const errData = await response.json();
  throw new Error(errData.detail || 'Failed to submit form');
}

setIsSuccess(true);

// Advanced GA4 Event
ReactGA.event("contact_form_submit", {
  service_selected: formData.service_type_id || "not_selected",
  budget_range: formData.budget_range || "not_specified",
  timeline: formData.timeline_id || "not_specified"
});
     

      setFormData({
        full_name: '',
        email: '',
        phone: '',
        service_type_id: '',
        budget_range: '',
        timeline_id: '',
        project_description: '',
      });

    } catch (err) {
      setError('Something went wrong. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-12"
        data-testid="form-success"
      >
        <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
          <CheckCircle size={40} className="text-primary" />
        </div>
        <h3 className="font-syne text-2xl font-bold text-white mb-3">
          Thank You!
        </h3>
        <p className="text-muted-foreground font-manrope text-base mb-6">
          We've received your request and will get back to you within 24 hours.
        </p>
        <Button
          onClick={() => setIsSuccess(false)}
          variant="outline"
          className="border-white/20 text-white hover:bg-white/10 font-manrope"
        >
          Submit Another Request
        </Button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
      {/* Row 1: Name & Email */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="full_name" className="text-white font-manrope">
            Full Name <span className="text-primary">*</span>
          </Label>
          <Input
            id="full_name"
            name="full_name"
            value={formData.full_name}
            onChange={handleChange}
            required
            placeholder="Your name"
            data-testid="input-full-name"
            className="bg-white/5 border-white/10 text-white placeholder:text-muted-foreground focus:border-primary"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="text-white font-manrope">
            Email <span className="text-primary">*</span>
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder="your@email.com"
            data-testid="input-email"
            className="bg-white/5 border-white/10 text-white placeholder:text-muted-foreground focus:border-primary"
          />
        </div>
      </div>

      {/* Row 2: Phone & Service Type */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="phone" className="text-white font-manrope">
            Phone Number <span className="text-muted-foreground text-xs">(Optional)</span>
          </Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+91 98765 43210"
            data-testid="input-phone"
            className="bg-white/5 border-white/10 text-white placeholder:text-muted-foreground focus:border-primary"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="service_type" className="text-white font-manrope">
            Service Type <span className="text-primary">*</span>
          </Label>
          <Select
            value={formData.service_type_id}
            onValueChange={(value) => handleSelectChange('service_type_id', value)}
             >
            <SelectTrigger
              data-testid="select-service-type"
              className="bg-white/5 border-white/10 text-white focus:border-primary"
            >
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent className="bg-background border-white/10">
              {serviceTypes.map((service) => (
            <SelectItem
              key={service.code}
              value={service.code}
              className="text-white hover:bg-white/10 focus:bg-white/10"
        >
              {service.name}
              </SelectItem>
       ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Row 3: Budget & Timeline */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="budget_range" className="text-white font-manrope">
            Budget Range <span className="text-muted-foreground text-xs">(Optional)</span>
          </Label>
          <Select
            value={formData.budget_range}
            onValueChange={(value) => handleSelectChange('budget_range', value)}
          >
            <SelectTrigger
              data-testid="select-budget"
              className="bg-white/5 border-white/10 text-white focus:border-primary"
            >
              <SelectValue placeholder="Select budget range" />
            </SelectTrigger>
            <SelectContent className="bg-background border-white/10">
              {BUDGET_RANGES.map((budget) => (
                <SelectItem
                  key={budget}
                  value={budget}
                  className="text-white hover:bg-white/10 focus:bg-white/10"
                >
                  {budget}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="timeline" className="text-white font-manrope">
            Timeline <span className="text-muted-foreground text-xs">(Optional)</span>
          </Label>
          <Select
            value={formData.timeline}
            onValueChange={(value) => handleSelectChange('timeline', value)}
          >
            <SelectTrigger
              data-testid="select-timeline"
              className="bg-white/5 border-white/10 text-white focus:border-primary"
            >
              <SelectValue placeholder="Select timeline" />
            </SelectTrigger>
            <SelectContent className="bg-background border-white/10">
              {TIMELINE_OPTIONS.map((time) => (
                <SelectItem
                  key={time}
                  value={time}
                  className="text-white hover:bg-white/10 focus:bg-white/10"
                >
                  {time}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Project Description */}
      <div className="space-y-2">
        <Label htmlFor="project_description" className="text-white font-manrope">
          Project Description <span className="text-primary">*</span>
        </Label>
        <Textarea
          id="project_description"
          name="project_description"
          value={formData.project_description}
          onChange={handleChange}
          required
          placeholder="Tell us about your project and any specific requirements..."
          rows={5}
          data-testid="input-description"
          className="bg-white/5 border-white/10 text-white placeholder:text-muted-foreground focus:border-primary resize-none"
        />
      </div>

      {error && (
        <p className="text-red-400 font-manrope text-sm" data-testid="form-error">
          {error}
        </p>
      )}

      <Button
        type="submit"
        disabled={isSubmitting}
        data-testid="submit-button"
        className="btn-glow bg-primary hover:bg-primary/90 text-white font-manrope w-full md:w-auto px-8"
      >
        {isSubmitting ? (
          <>
            <Loader2 size={18} className="mr-2 animate-spin" />
            Submitting...
          </>
        ) : (
          <>
            Submit Request
            <ArrowRight size={18} className="ml-2" />
          </>
        )}
      </Button>
    </form>
  );
};

const ContactInfo = () => {
  const contactMethods = [
    {
      icon: Mail,
      label: 'Email',
      value: CONTACT_INFO.email,
      href: `mailto:${CONTACT_INFO.email}`,
    },
    {
      icon: Phone,
      label: 'Phone / WhatsApp',
      value: CONTACT_INFO.phone,
      href: `tel:${CONTACT_INFO.phone}`,
    },
    {
      icon: Instagram,
      label: 'Instagram',
      value: '@hydroxstudios',
      href: CONTACT_INFO.instagram,
      external: true,
    },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h3 className="font-syne text-xl font-semibold text-white mb-2">
          Get in Touch
        </h3>
        <p className="text-muted-foreground font-manrope text-sm">
          Prefer to reach out directly? Here's how you can contact us.
        </p>
      </div>

      <div className="space-y-4">
        {contactMethods.map((method) => {
          const Icon = method.icon;
          return (
            <a
              key={method.label}
              href={method.href}
              target={method.external ? '_blank' : undefined}
              rel={method.external ? 'noopener noreferrer' : undefined}
              className="flex items-center gap-4 p-4 rounded-lg bg-white/5 border border-white/10 hover:border-primary/50 transition-colors group"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <Icon size={20} className="text-primary" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground font-manrope">
                  {method.label}
                </p>
                <p className="text-white font-manrope">
                  {method.value}
                </p>
              </div>
            </a>
          );
        })}
      </div>

      {/* WhatsApp CTA */}
      <div className="pt-4">
        <Button
          data-testid="whatsapp-cta"
          variant="outline"
          className="w-full border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10 font-manrope"
          onClick={() => {
            ReactGA.event({
            category: "Lead",
            action: "WhatsApp Click",
            label: "WhatsApp Direct Button"
        });
            const phoneNumber = CONTACT_INFO.whatsapp.replace('+', '');
            const message = encodeURIComponent("Hi, I'm interested in your services.");
            window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank', 'noopener,noreferrer');
          }}
        >
          Chat on WhatsApp
          <ArrowRight size={16} className="ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default function Contact() {
  return (
    <div data-testid="contact-page">
      <SEO 
        title="Contact Hydrox Studios | Music Video Production in Guwahati"
        description="Contact Hydrox Studios, a Guwahati-based music video and audio production studio serving artists across Assam and Northeast India."
        path="/contact"
      />

      {/* Hero Section */}
      <section className="relative min-h-[40vh] flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent" />
        <Particles count={15} />

        <div className="relative z-10 text-center px-6 max-w-4xl">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-sm uppercase tracking-[0.3em] text-primary font-manrope mb-6"
          >
            Contact
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="font-syne text-4xl sm:text-5xl md:text-6xl font-bold text-white tracking-tight mb-6"
          >
            Contact Hydrox Studios
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="font-manrope text-base md:text-lg text-muted-foreground max-w-2xl mx-auto"
          >
            Tell us about your music video or audio production project in
            Guwahati, Assam. We respond within 24 hours with a structured
            proposal.
          </motion.p>
        </div>
      </section>

      {/* Main Section */}
      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
          
          {/* Form */}
          <div className="lg:col-span-2">
            <GlassCard className="p-8 md:p-10" hover={false}>
              <ContactForm />
            </GlassCard>
          </div>

          {/* Contact Info */}
          <div className="space-y-8">
            <ContactInfo />

            {/* Business Info Block */}
            <div className="p-6 bg-white/5 border border-white/10 rounded-lg">
              <h4 className="font-syne text-lg font-semibold text-white mb-3">
                Studio Location
              </h4>

              <p className="text-muted-foreground text-sm leading-relaxed">
                Hydrox Studios <br />
                Based in Guwahati, Assam <br />
                Serving clients across Northeast India
              </p>
            </div>

            {/* Google Map Embed */}
            <div className="rounded-lg overflow-hidden border border-white/10">
              <iframe
                title="Hydrox Studios Guwahati Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d15281513.599027047!2d72.11033709507376!3d20.7573478286956!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xcb016e144723633%3A0xae131349daa17abe!2sHydrox%20Studios!5e0!3m2!1sen!2sin!4v1772221447281!5m2!1sen!2sin"
                width="100%"
                height="250"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
              ></iframe>
            </div>
          </div>

        </div>
      </Section>
    </div>
  );
}
